/**
 * CheckoutButton Component - Trade Loop v2.0
 *
 * Handles subscription plan changes:
 * - NEW SUBSCRIPTIONS: Creates Stripe checkout for Starter users
 * - UPGRADES: Immediate upgrade with proration (charges prorated difference immediately)
 * - DOWNGRADES: Schedules plan change for next billing cycle (no immediate charge)
 * - Handles loading states and errors
 *
 * ## Native builds never buy anything in the app (Card 18, task A)
 *
 * Every path below that MOVES MONEY is unreachable on iOS and Android. Two shapes are barred,
 * not one:
 *
 *  1. `window.location.href = checkoutUrl`. In a bundled build that navigates the WebView
 *     itself to Stripe — no browser chrome, no way back — which is exactly the "app uses its own
 *     mechanism to unlock content" shape Apple 3.1.1 and Google's billing policy both police.
 *  2. The immediate prorated upgrade, which charges the saved card server-side with no redirect
 *     at all. There is no WebView navigation to catch here, and that makes it easier to miss:
 *     it is still collecting payment for digital content from inside the app. The post-Epic
 *     ruling permits *linking out* from US storefront apps; it does not permit taking the money
 *     in-app. So on native this is routed out to the web like any other purchase.
 *
 * What stays in the app on native: DOWNGRADES and CANCELLATIONS. No money is collected, and both
 * stores expect a working plan-management and cancellation path to exist.
 *
 * Outside the US storefront, native renders no purchase call to action at all — see
 * `useCheckoutLinkOut`, which fails closed so an unknown region is treated as non-US.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { UpgradeConfirmationModal } from './UpgradeConfirmationModal';
import { ExternalCheckoutModal } from './ExternalCheckoutModal';
import { useCheckoutLinkOut } from '@/hooks/useCheckoutLinkOut';
import { isNative } from '@/utils/platform';
import { isPlanPurchasable } from '@/config/subscription-plan-limits';

interface CheckoutButtonProps {
  /**
   * The plan this button targets.
   *
   * BOTH SPELLINGS ARE REAL. `PlansPage` passes its `planType` straight through, which is
   * 'pro' | 'grow'; other call sites use the older UI name 'core' for the same plan. Normalize
   * with `toApiPlan` below rather than testing for one of them — a `plan === 'core' ? ... : ...`
   * silently resolves 'pro' to Grow, which is exactly the bug this replaced.
   */
  plan: 'core' | 'pro' | 'grow';
  buttonText?: string;
  disabled?: boolean;
  className?: string;
}

export function CheckoutButton({
  plan,
  buttonText = 'Subscribe',
  disabled = false,
  className
}: CheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [proratedCost, setProratedCost] = useState<{
    fullAmount: number;
    proratedAmount: number | null;
    currentPlan: string;
    newPlan: string;
  } | null>(null);

  const [showLinkOutModal, setShowLinkOutModal] = useState(false);

  const { showError, showInfo, showSuccess } = useToast();
  const { user } = useAuth();
  const {
    eligibility,
    isCheckingEligibility,
    handoffUrl,
    isPreparing,
    prepareError,
    prepareHandoff,
    reset: resetHandoff
  } = useCheckoutLinkOut();

  // Determine if user has an active subscription (not on starter)
  const hasActiveSubscription = user?.subscriptionPlan &&
    user.subscriptionPlan.toLowerCase() !== 'starter';

  // 'core' is the legacy UI name for Pro. Mapped explicitly, never by elimination: PlansPage
  // passes 'pro', so `plan === 'core' ? 'pro' : 'grow'` turned every "Upgrade to Pro" tap into a
  // Grow request — which the API rejects outright with 400 while PLAN_VISIBILITY.grow is false.
  const targetPlan: 'pro' | 'grow' = plan === 'grow' ? 'grow' : 'pro';
  const planLabel = targetPlan === 'grow' ? 'Grow' : 'Pro';

  /**
   * Is the tapped plan above the user's current one? Only upward moves take money, and only
   * those are diverted to the web on native.
   */
  const isUpgradeOrNew = (() => {
    const planHierarchy = ['starter', 'pro', 'grow'];
    const currentIndex = planHierarchy.indexOf(user?.subscriptionPlan?.toLowerCase() || 'starter');
    return planHierarchy.indexOf(targetPlan) > currentIndex;
  })();

  /**
   * Would this tap start a purchase of a plan that is not for sale? (Card 24)
   *
   * Every call site is already meant to have stopped rendering a Grow purchase button, which is
   * exactly why this lives here too: this component is the single place all of them funnel
   * through, and a guard that depends on every caller remembering is a guard that gets missed by
   * the next caller. Scoped to upward moves only — a Grow subscriber must keep being able to
   * downgrade and cancel.
   */
  const wouldPurchaseUnavailablePlan = isUpgradeOrNew && !isPlanPurchasable(targetPlan);

  /**
   * Open the disclosure and start minting in the same tap.
   *
   * The mint has to finish before the user's *next* tap rather than this one: WKWebView only
   * routes `window.open` to the system browser for a user-gesture-initiated open, so the
   * confirm button needs a URL already in hand. See useCheckoutLinkOut for the full reasoning.
   */
  const handleNativeLinkOut = () => {
    setShowLinkOutModal(true);
    void prepareHandoff(targetPlan);
  };

  const closeLinkOutModal = () => {
    setShowLinkOutModal(false);
    // Do not leave a live hand-off token sitting in component state once the sheet is gone.
    resetHandoff();
  };

  const calculateProratedCost = async () => {
    setIsLoading(true);

    try {
      const currentPlan = user?.subscriptionPlan?.toLowerCase();
      const currentStatus = user?.subscriptionStatus?.toLowerCase();

      console.log('[CheckoutButton] Calculating cost:', {
        targetPlan,
        currentPlan,
        currentStatus,
        planProp: plan
      });

      // Determine if this is an upgrade or downgrade
      const planHierarchy = ['starter', 'pro', 'grow'];
      const currentIndex = planHierarchy.indexOf(currentPlan || 'starter');
      const targetIndex = planHierarchy.indexOf(targetPlan);
      const isUpgrade = targetIndex > currentIndex;

      console.log('[CheckoutButton] Plan direction:', {
        currentIndex,
        targetIndex,
        isUpgrade,
        planHierarchy
      });

      if (!isUpgrade) {
        // This is a downgrade - handle differently
        handleDowngrade(targetPlan);
        return;
      }

      // Calculate prorated cost for upgrades
      const costData = await api.post('/api/workflows/subscriptions/calculate-prorated-cost', {
        newPlan: targetPlan
      });

      if (costData.success) {
        setProratedCost({
          fullAmount: costData.data.fullAmount,
          proratedAmount: costData.data.proratedAmount,
          currentPlan: costData.data.currentPlan,
          newPlan: costData.data.newPlan
        });
        setShowConfirmModal(true);
      }
    } catch (error) {
      console.error('Cost calculation error:', error);
      showError(
        'Failed to Calculate Cost',
        error instanceof Error ? error.message : 'Unable to calculate upgrade cost'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDowngrade = async (targetPlan: string) => {
    try {
      const data = await api.post('/api/workflows/subscriptions/change-plan', {
        newPlan: targetPlan
      });

      if (data.success) {
        showSuccess(
          'Plan Change Scheduled',
          `Your plan will downgrade to ${targetPlan === 'grow' ? 'Grow' : 'Pro'} at your next billing date. You'll keep your current plan access until then.`
        );
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      showError(
        'Plan Change Failed',
        error instanceof Error ? error.message : 'Unable to schedule plan change'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!proratedCost) return;

    setIsLoading(true);

    try {
      const data = await api.post('/api/workflows/subscriptions/upgrade-immediately', {
        newPlan: proratedCost.newPlan
      });

      if (data.success) {
        setShowConfirmModal(false);
        showSuccess(
          'Plan Upgraded!',
          `Your plan has been upgraded to ${planLabel} immediately. You have immediate access to all new features.`
        );
        // Reload page to refresh user data and show new features
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      showError(
        'Upgrade Failed',
        error instanceof Error ? error.message : 'Unable to upgrade plan'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    // ⛔ Card 24, at the handler rather than only on the `disabled` prop. `disabled` is a
    // rendering detail; this is the branch a devtools edit or a stale bundle has to get past.
    if (wouldPurchaseUnavailablePlan) {
      return;
    }

    // ⛔ THE GUARD. On native, anything that would take money leaves the app instead. Placed
    // first and covering every branch below on purpose: a guard added per-branch is a guard
    // somebody forgets to add to the next branch, and one missed call site is a store rejection.
    if (isNative && isUpgradeOrNew) {
      handleNativeLinkOut();
      return;
    }

    if (hasActiveSubscription) {
      const currentStatus = user?.subscriptionStatus?.toLowerCase();

      // Trial users - redirect to Stripe checkout
      if (currentStatus === 'trialing') {
        setIsLoading(true);
        try {
          const origin = window.location.origin;
          const data = await api.post('/api/workflows/subscriptions/checkout', {
            plan,
            successUrl: `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${origin}/payment-cancel`
          });

          if (data.success && data.data.checkoutUrl) {
            showInfo('Redirecting to checkout...', 'Please wait');
            window.location.href = data.data.checkoutUrl;
          } else {
            throw new Error('No checkout URL received');
          }
        } catch (error) {
          console.error('Checkout error:', error);
          showError(
            'Checkout Failed',
            error instanceof Error ? error.message : 'Unable to process request'
          );
          setIsLoading(false);
        }
        return;
      }

      // Existing paying subscribers - calculate cost and show confirmation
      await calculateProratedCost();
    } else {
      // New subscriber: Create Stripe checkout session
      setIsLoading(true);
      try {
        const origin = window.location.origin;

        const data = await api.post('/api/workflows/subscriptions/checkout', {
          plan,
          successUrl: `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/payment-cancel`
        });

        // Redirect to Stripe checkout
        if (data.success && data.data.checkoutUrl) {
          showInfo('Redirecting to checkout...', 'Please wait');
          window.location.href = data.data.checkoutUrl;
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (error) {
        console.error('Checkout error:', error);
        showError(
          'Checkout Failed',
          error instanceof Error ? error.message : 'Unable to process request'
        );
        setIsLoading(false);
      }
    }
  };

  const PLAN_PRICES = {
    core: 29, // Pro plan
    grow: 79
  };

  // ── Non-US native: plan state, read-only, no call to action ──────────────────────────────
  //
  // Apple 3.1.1 still bans buttons, external links and other calls to action in EVERY storefront
  // except the United States, and Google's program limits external content links to the US and
  // its territories. The store-level availability settings (Cards 11 and 12) are the primary
  // enforcement; this is the runtime layer underneath, and it is what lets availability widen
  // later without a resubmission scramble.
  //
  // It is dead code during a US-only beta BY DESIGN. Do not delete it as unused.
  //
  // `isCheckingEligibility` renders nothing rather than a disabled button: a CTA that appears
  // and then vanishes is worse than one that arrives a beat late, and it fails closed.
  // ── Plan not for sale yet: state, read-only, no call to action (Card 24) ──────────────────
  //
  // Checked BEFORE the region branch because it applies on web as well as native, and before any
  // network state because it does not depend on one. Renders a plainly inert control rather than
  // nothing, so a caller that still asks for a Grow button gets a visibly unavailable one rather
  // than a silent gap where a purchase used to be.
  if (wouldPurchaseUnavailablePlan) {
    return (
      <Button disabled aria-disabled="true" className={className}>
        Coming Soon
      </Button>
    );
  }

  if (isNative && isUpgradeOrNew && !eligibility.allowed) {
    if (isCheckingEligibility) return null;

    return (
      <p className={`text-sm text-gray-500 text-center ${className ?? ''}`}>
        Subscriptions aren't available from the app in your region yet.
      </p>
    );
  }

  return (
    <>
      <Button
        onClick={handleCheckout}
        disabled={disabled || isLoading}
        className={className}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          buttonText
        )}
      </Button>

      {/* Native only. The web keeps the ordinary in-page redirect, which is lawful there and a
          better experience — no store has any say over a browser. */}
      {isNative && (
        <ExternalCheckoutModal
          isOpen={showLinkOutModal}
          onClose={closeLinkOutModal}
          planName={planLabel}
          handoffUrl={handoffUrl}
          shortUrl={eligibility.shortUrl}
          isPreparing={isPreparing}
          error={prepareError}
        />
      )}

      {proratedCost && (
        <UpgradeConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmUpgrade}
          isLoading={isLoading}
          currentPlan={proratedCost.currentPlan.charAt(0).toUpperCase() + proratedCost.currentPlan.slice(1)}
          newPlan={proratedCost.newPlan.charAt(0).toUpperCase() + proratedCost.newPlan.slice(1)}
          proratedAmount={proratedCost.proratedAmount}
          fullAmount={proratedCost.fullAmount}
        />
      )}
    </>
  );
}
